/**
 * Работа со строками
 * @packageDocumentation
 */

import { getDomainRegexp } from '@/core/utils/check';
import { useI18nLang } from '@/core/plugins/i18n';

export const REGEXP_START_SYMBOL_ICON = new RegExp('^[^a-zа-я0-9_%#!?.:\'"«»[\\](){} \\r\\n\\t<>&/+-]', 'i');

/**
 * Извлекает иконку и оставшийся контент из строки
 *
 * Возвращает объект со свойствами:
 * - `icon`: содержит символ иконки, если он присутствует, иначе `null`
 * - `content`: содержит оставшийся текст без символа иконки, если он был извлечен, или всю строку
 */
export const extractIconAndContent = (content: string) => {
	const iconExists = content?.[0]?.match(REGEXP_START_SYMBOL_ICON);

	return {
		icon: iconExists ? content[0] : null,
		content: iconExists ? content.substring(1) : content,
	};
};

/**
 * Сгенерировать числовой хэш от строки для идентификатора
 */
export const genIntHash = (string: string): number => {
	string += '';

	let hash = 0;
	if (!string.length) return hash;

	for (let i = 0; i < string.length; i++) {
		const char = string.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash;
	}

	if (hash < 0) hash *= -1;

	return hash;
};

/**
 * Проверить что переданная строка является URL
 */
export const isUrl = (url: string): boolean => {
	return /^\w+:\/\/([\w-]+\.)+[\w-]+(:\d+)?(\/|$)/.test(url);
};

/**
 * Добавляет пробелы между разрядами если требуется
 * @param nStr строка с числом
 */
export const addCommasWhite = (n: number | string): string => {
	let nStr = n + '';

	nStr = nStr.replace('-', '–');

	let x = nStr.split('.');
	let x1 = x[0];
	let x2 = (x.length > 1) ? ('.' + x[1]) : '';

	if (x1.replace(/[+–]/, '').length <= 4) return nStr;

	const rgx = /(\d+)(\d{3})/;
	while (rgx.test(x1)) {
		x1 = x1.replace(rgx, '$1' + ' ' + '$2');
	}
	return x1 + x2;
};

/**
 * Вставляет HTML-код разрыва строки перед каждым переводом строки
 */
export const nl2br = (str: string): string => {
	if (str === undefined) return '';

	return str.replace(/\r\n|\r|\n/g, '<br>');
};

/**
 * Перевести число без деситичной приставки в число с деситичной приставкой степеней тысячи (с параметрами по умолчанию 1255 -> '1.26 K')
 * @param number
 * @param precision - точность, количество знаков после запятой
 * @param dimension - размерность, по умолчанию 1000
 * @param suffix - суффикс к десятичной приставке (по умолчанию выводится латинская заглавная буква)
 */
export const numberWithWord = (number: number, precision = 2, dimension = 1000, suffix = ''): string => {
	const k = Math.pow(10, precision);

	let numberStr = String(number);
	if (Math.abs(number) > Math.pow(dimension, 3)) {
		numberStr = Math.round(number / Math.pow(dimension, 3) * k) / k + ' <small>G' + suffix + '</small>';
	} else if (Math.abs(number) > Math.pow(dimension, 2)) {
		numberStr = Math.round(number / Math.pow(dimension, 2) * k) / k + ' <small>M' + suffix + '</small>';
	} else if (Math.abs(number) > dimension) {
		numberStr = Math.round(number / dimension * k) / k + ' <small>K' + suffix + '</small>';
	} else {
		numberStr = Math.round(number * k) / k + '';
	}

	numberStr = numberStr.replace(/^(-?\d+)(\d{3})/, '$1 $2');

	return numberStr;
};

/**
 * Обрезать текст под три точки
 * @param string
 * @param max_length - максимальное количество символов текста, остальное обрежеться
 * @param pos - область обрезки 0-начало | 1-середина | 2-конец строки
 */
export const ellipsis = (string: string, max_length: number, pos: 0 | 1 | 2 = 1): string => {
	if (string == undefined) return '';

	string += '';
	if (max_length == undefined || string.length <= max_length) return string;

	if (pos === 0) {
		string = string.substr(string.length - max_length, max_length);
		string = '...' + string;
	} else if (pos === 1) {
		const string1 = string.substr(0, max_length / 2);
		const string2 = string.substr(string.length - max_length / 2, Math.ceil(max_length / 2));
		string = string1 + '...' + string2;
	} else if (pos === 2) {
		string = string.substr(0, max_length);
		string = string + '...';
	}

	return string;
};

/**
 * Сгенерировать строку текста для вывода количества в разном склонении
 * @param num
 * @param ending0 - родительный падеж, множ. число (10 модулей)
 * @param ending1 - именительный падеж, ед. число (1 модуль)
 * @param ending2 - винительный падеж, множ. число (3 модуля)
 */
export const numberEnding = (num: number, ending0: string, ending1: string, ending2: string): string => {
	// склонение по множественному числу
	if (useI18nLang().value === 'en') {
		if (num === 1) {
			return ending1;
		} else {
			return ending0;
		}
	}

	const num100 = num % 100;
	const num10 = num % 10;

	if (num100 >= 5 && num100 <= 20) {
		return ending0;
	} else if (num10 === 0) {
		return ending0;
	} else if (num10 === 1) {
		return ending1;
	} else if (num10 >= 2 && num10 <= 4) {
		return ending2;
	} else if (num10 >= 5 && num10 <= 9) {
		return ending0;
	} else {
		return ending2;
	}
};

/**
 * Перевести строку в родительный падеж
 */
export const toRoditPadej = (str: string): string => {
	if (useI18nLang().value != 'ru' || str == '') return str;

	const str_array = str.split(' ');

	$.map(str_array, function (str, index) {
		str = str + 'а';
		str = str.replace('оа', 'о');
		str = str.replace('аяа', 'ой');
		str = str.replace('шийа', 'шого');
		str = str.replace('ыйа', 'ого');
		str = str.replace('йа', 'я');
		str = str.replace('ьа', 'я');
		str = str.replace('яа', 'и');
		str = str.replace('оваа', 'овой');
		str = str.replace('аа', 'ы');
		str = str.replace('кия', 'кого');
		str = str.replace('каи', 'кой');

		str_array[index] = str;
	});

	str = str_array.join(' ');

	return str;
};

/**
 * Транслитерация текста
 */
export const rusToLatin = (str: string): string => {
	const ru: { [index: string]: string } = {
		'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd',
		'е': 'e', 'ё': 'e', 'ж': 'j', 'з': 'z', 'и': 'i',
		'й': 'i', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n',
		'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't',
		'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'ch',
		'ш': 'sh', 'щ': 'shch', 'ъ': '', 'ы': 'y', 'ь': '',
		'э': 'e', 'ю': 'u', 'я': 'ya',
	};

	const str_array = str.split(' ');

	$.map(str_array, function (str, index) {
		const newStr: string[] = [];
		for (let i = 0; i < str.length; ++i) {
			newStr.push(
				ru[str[i]] || ru[str[i].toLowerCase()] == undefined && str[i] || ru[str[i].toLowerCase()].replace(/^(.)/, function (match) {
					return match.toUpperCase();
				}),
			);
		}

		str_array[index] = newStr.join('');
	});

	str = str_array.join(' ');

	return str;
};

/**
 * Замена html-символов (`&`, `<`, `>`, `"`, `'`) на имена литер (`&` -> `&amp;`)
 */
export const htmlspecialchars = (str: string): string => {
	str = str
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');

	return str;
};

/**
 * Выделить нужное в тексте желтым цветом
 * поиск происходит по строке fieldFilter.values[0]
 */
export const highlightHtml = (text: string, fieldFilter: { operator: 'CONTAINS' | 'REGEXP', values: string[] }): string => {
	let regExp: RegExp | null = null;

	switch (fieldFilter.operator) {
		case 'CONTAINS':
		case 'REGEXP':
			try {
				let regExpString = fieldFilter.values[0];
				if (fieldFilter.operator != 'REGEXP') regExpString = regExpString.replace(/([()\[\]])/g, '\\$1');

				regExp = new RegExp('(' + regExpString + ')', 'gi');
			} catch (exception) {
				return text;
			}

			break;
	}

	text = text.replace(regExp!, '<b class="found">$1</b>');

	return text;
};

/**
 * Пример: logHTTPCodesHundreds => log_http_codes_hundreds
 */
export const camelToSnakeCase = (str: string): string | undefined => {
	return str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)?.map(token => token.toLowerCase()).join('_');
};

/**
 * Получить url изображения флага, по коду страны по стандарту ISO 3166-1 alpha-2
 *
 * @todo Опубликовать в CDN или брать c публичного CDN
 */
export const genFlagLinkByCountryCode = (countryCode: string) => {
	const urlImage = '/cloud/common/flags/' + countryCode.toUpperCase() + '.svg';
	return `/controller/file/?src=${urlImage}`;
};

/**
 * Обернуть все ссылки и email в тексте в html-тег `<a>`.
 *
 * Для email ставиться префикс `mailto:`
 */
export const addLinkTags = (text: string): string => {
	const domainRegexp = getDomainRegexp();
	const afterDomainRegexp = '(([\\/?\\w&=%+#;:-]*(\\.\\w+)?))*';

	const regexpMailto = new RegExp(`(^|[\\s>(),])([a-z0-9а-яё_.]+@${domainRegexp}${afterDomainRegexp})`, 'gui');
	text = text.replace(regexpMailto, `$1<a href="mailto:$2" target="_blank" rel="noopener">↑[$2]↓</a>`);

	const regexpLink = new RegExp(`(^|[\\s>(),])(https?:\/\/)?(${domainRegexp}${afterDomainRegexp})`, 'gui');
	text = text.replace(regexpLink, `$1<a href="√$2$3" target="_blank" rel="nofollow noopener">$2$3</a>`);

	text = text.replaceAll('√http', 'http');
	text = text.replaceAll('√', 'http://');
	text = text.replaceAll(/(↑\[|]↓)/g, '');

	return text;
};

/**
 * Перевести первую букву строки в верхний регистр
 */
export const toCapitalize = (str: string): string => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Получить случайную строку
 */
export const getRandomHash = (length: number = 20): string => {
	const l = 'AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZz1234567890';

	let result = '';
	for (let i = 0; i < length; i++) {
		result += l[Math.floor(Math.random() * l.length)];
	}

	return result;
};
